import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

/**
 * Attaches the session token to every Banku API request and watches responses for an expired
 * session. The backend reports an expired session as HTTP 200 with `{ err: true, type: 'Token' }`
 * (it calls PHP's `die()` before setting a status code), so a real HTTP error is not guaranteed.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const isApiRequest = req.url.startsWith(environment.apiUrl);
  const token = auth.getToken();
  const authReq = isApiRequest && token ? req.clone({ setHeaders: { Authorization: token } }) : req;

  return next(authReq).pipe(
    tap((event) => {
      if (isApiRequest && event instanceof HttpResponse) {
        const body = event.body as { err?: boolean; type?: string } | null;
        if (body?.err === true && body.type === 'Token') {
          expireSession(auth, router);
        }
      }
    }),
    catchError((err: unknown) => {
      if (isApiRequest && err instanceof HttpErrorResponse && err.status === 401) {
        expireSession(auth, router);
      }
      return throwError(() => err);
    }),
  );
};

function expireSession(auth: AuthService, router: Router): void {
  if (!auth.isAuthenticated()) return;
  auth.handleSessionExpired();
  router.navigateByUrl('/auth/login', { replaceUrl: true });
}
